import { useRef } from "react";
import { resolveAsset } from "../../lib/api";

const getSelectionOffsets = (container) => {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) return null;
  const pre = range.cloneRange();
  pre.selectNodeContents(container);
  pre.setEnd(range.startContainer, range.startOffset);
  const start = pre.toString().length;
  const len = range.toString().length;
  if (len === 0) return null;
  return [start, start + len];
};

const mergeRanges = (ranges) => {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const out = [];
  for (const r of sorted) {
    if (out.length && r[0] <= out[out.length - 1][1]) {
      out[out.length - 1][1] = Math.max(out[out.length - 1][1], r[1]);
    } else {
      out.push([...r]);
    }
  }
  return out;
};

export const QuestionStem = ({ stem, imageUrl, highlights = [], onChange, enabled = true }) => {
  const ref = useRef(null);

  const handleMouseUp = () => {
    if (!enabled || !onChange || !ref.current) return;
    const r = getSelectionOffsets(ref.current);
    if (!r) return;
    onChange(mergeRanges([...highlights, r]));
    window.getSelection().removeAllRanges();
  };

  const removeRange = (idx) => {
    if (!enabled || !onChange) return;
    onChange(highlights.filter((_, i) => i !== idx));
  };

  const sorted = mergeRanges(highlights);
  const segs = [];
  let cur = 0;
  sorted.forEach((r, i) => {
    const [s, e] = [Math.max(0, Math.min(r[0], stem.length)), Math.max(0, Math.min(r[1], stem.length))];
    if (s > cur) segs.push({ text: stem.slice(cur, s) });
    segs.push({ text: stem.slice(s, e), hl: i });
    cur = e;
  });
  if (cur < stem.length) segs.push({ text: stem.slice(cur) });

  return (
    <div>
      {imageUrl && (
        <img
          src={resolveAsset(imageUrl)}
          alt="Question exhibit"
          data-testid="question-exhibit"
          className="mb-5 w-full max-w-2xl rounded-lg border border-slate-200 bg-white"
        />
      )}
      <p
        ref={ref}
        onMouseUp={handleMouseUp}
        data-testid="question-stem"
        className="text-base sm:text-lg text-slate-900 leading-relaxed select-text"
        title={enabled ? "Select text to highlight; click a highlight to remove it" : undefined}
      >
        {segs.map((s, i) =>
          s.hl != null ? (
            <mark
              key={i}
              data-testid="stem-highlight"
              onClick={() => removeRange(s.hl)}
              className="bg-yellow-200 cursor-pointer hover:bg-yellow-300 transition-colors"
            >
              {s.text}
            </mark>
          ) : (
            <span key={i}>{s.text}</span>
          )
        )}
      </p>
    </div>
  );
};
